import React, { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("adminUser");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        console.error("Failed to parse saved user:", err);
        localStorage.removeItem("adminUser");
      }
    }
    setLoading(false);
    setLoading(false);
  }, []);

  // Periodic check for token expiry
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      const { expired, warning } = checkTokenExpiry(token);

      if (expired && !sessionExpired) {
        setSessionExpired(true);
        logout("Session expired"); // Auto logout if expired
      } else if (warning && !showExpiryWarning) {
        setShowExpiryWarning(true); // Show warning if imminent
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [token, sessionExpired, showExpiryWarning]);

  // Check token expiry
  const checkTokenExpiry = (jwtToken) => {
    if (!jwtToken) return { expired: true, warning: false };

    try {
      const [, payloadBase64] = jwtToken.split(".");
      const decoded = JSON.parse(atob(payloadBase64));
      const expiry = decoded.exp * 1000; // ms
      const currentTime = Date.now();
      const timeUntilExpiry = expiry - currentTime;

      return {
        expired: timeUntilExpiry <= 0,
        warning: timeUntilExpiry > 0 && timeUntilExpiry <= 60000, // 1 min warning
        timeUntilExpiry,
      };
    } catch (err) {
      return { expired: true, warning: false };
    }
  };

  const login = async (jwtToken, userData = null) => {
    console.log("[AUTH] Login function called");
    localStorage.setItem("adminToken", jwtToken);
    setToken(jwtToken);
    setSessionExpired(false);
    setShowExpiryWarning(false);

    if (userData) {
      setUser(userData);
      localStorage.setItem("adminUser", JSON.stringify(userData));
      // Also update global config with admin user data
      config.setUserData(userData);
    }

    console.log("[AUTH] Navigating to dashboard...");
    navigate("/");
  };

  const logout = (message = "") => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setToken(null);
    setUser(null);
    setShowExpiryWarning(false);
    // Clear global config
    config.clearUserData();

    if (message) {
      setSessionExpired(true);
    }
    navigate("/authentication/sign-in");
  };

  const dismissWarning = () => {
    setShowExpiryWarning(false);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        loading,
        sessionExpired,
        showExpiryWarning,
        dismissWarning,
        checkTokenExpiry,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

